import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Image as ImageIcon, Tags } from 'lucide-react';
import type { StatusToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useStatusesList, useStatusMutations } from '~/hooks/use-statuses';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

/** Product statuses (Καταστάσεις) list — thumbnail + code/name + flags + sort; standalone editor on add/edit. */
export function StatusesGridView() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data, isLoading, isError, refetch } = useStatusesList();
	const { remove } = useStatusMutations();

	const [deleteRow, setDeleteRow] = useState<StatusToken | null>(null);

	const columns = useMemo<SystemGridColumn<StatusToken>[]>(
		() => [
			{
				key: 'image',
				header: t('catalog.statuses.columns.image'),
				width: '70px',
				toggleable: false,
				render: r =>
					r.img1 ? (
						<img src={r.img1} alt={r.nm ?? ''} className="h-9 w-9 rounded border border-border object-cover" />
					) : (
						<div className="flex h-9 w-9 items-center justify-center rounded border border-border bg-elevated">
							<ImageIcon size={14} className="text-foreground-disabled" />
						</div>
					)
			},
			{
				key: 'code',
				header: t('catalog.statuses.columns.code'),
				width: '200px',
				sortValue: r => r.cd ?? '',
				exportValue: r => r.cd ?? '',
				render: r => <span className="truncate font-mono text-xs text-foreground-muted">{r.cd || '—'}</span>
			},
			{
				key: 'name',
				header: t('catalog.statuses.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: r => r.nm ?? '',
				exportValue: r => r.nm ?? '',
				render: r => (
					<span className="inline-flex items-center gap-2 truncate text-sm font-medium text-foreground" title={r.nm ?? ''}>
						{r.chex && <span className="h-3 w-3 shrink-0 rounded-full border border-border" style={{ backgroundColor: r.chex }} />}
						{r.nm || '—'}
					</span>
				)
			},
			{
				key: 'disableCart',
				header: t('catalog.statuses.columns.disableCart'),
				width: '140px',
				align: 'center',
				defaultVisible: false,
				sortValue: r => (r.dshc ? 1 : 0),
				exportValue: r => (r.dshc ? t('common.yes') : t('common.no')),
				render: r => <Bool on={Boolean(r.dshc)} />
			},
			{
				key: 'showAsTag',
				header: t('catalog.statuses.columns.showAsTag'),
				width: '120px',
				align: 'center',
				sortValue: r => (r.s_as_tag ? 1 : 0),
				exportValue: r => (r.s_as_tag ? t('common.yes') : t('common.no')),
				render: r => <Bool on={Boolean(r.s_as_tag)} />
			},
			{
				key: 'visible',
				header: t('catalog.statuses.columns.visible'),
				width: '90px',
				align: 'center',
				sortValue: r => (r.iv ? 1 : 0),
				exportValue: r => (r.iv ? t('common.yes') : t('common.no')),
				render: r => <Bool on={Boolean(r.iv)} />
			},
			{
				key: 'sort',
				header: t('catalog.statuses.columns.sort'),
				width: '80px',
				align: 'right',
				sortValue: r => r.si ?? 0,
				exportValue: r => r.si ?? 0,
				render: r => <span className="text-sm tabular-nums text-foreground-muted">{r.si ?? 0}</span>
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<StatusToken>
				gridId="statuses"
				title={t('catalog.statuses.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={r => r.id ?? r.nm}
				columns={columns}
				searchPredicate={(r, q) => `${r.nm ?? ''} ${r.cd ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<Tags size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('catalog.statuses.add')}
				onAdd={() => navigate('/products/statuses/create')}
				onEdit={r => navigate(`/products/statuses/${r.id}`)}
				onDelete={setDeleteRow}
				exportFilename="statuses"
			/>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('catalog.statuses.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('catalog.statuses.deleteBody', { name: deleteRow?.nm ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
